import type { FC } from "react";
import { useMemo, useState, useCallback } from "react";

import type { ILegendItems, ILegendItem } from "app/components/React/Legends/legendsTypes";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import { getLegends } from "../../../ChannelsOverviewPoPChart/PoPCompareChart/view-by-domain-pop-chart/utils";
import { ChannelsOverviewBarChart } from "../../ChannelsOverviewBarChart";
import type { IDomainsData } from "../../types";
import { TrafficSourceDisplayMap } from "../../types";
import { processDataDomainToShow } from "../../utils/chartHelpers";
import ChartWrapperWithKey from "../../utils/ChartWrapperWithKey";
import { transformDomainsData } from "../../utils/transformDomainsData";

import { getChartConfig } from "./getChartConfig";
import { LegendContainer, ViewByDomainChartWrapper } from "./StyledComponents";
import type { IViewByDomainChart } from "./types";
import { getInitialLegends } from "./utils";

const ViewByDomainChart: FC<IViewByDomainChart> = ({
    data,
    isPercentageDataMode,
    childrenAboveLegend,
    trafficSourcesMap = TrafficSourceDisplayMap,
}) => {
    const transformedData = useMemo(() => transformDomainsData(data, trafficSourcesMap), [data, trafficSourcesMap]);

    const [legends, setLegends] = useState<ILegendItems>(() => {
        const initialSeries = processDataDomainToShow(transformedData, data, isPercentageDataMode, trafficSourcesMap);
        return getInitialLegends(initialSeries, trafficSourcesMap);
    });

    const visibleData = useMemo(() => {
        const visibleKeys = legends.filter((legend) => legend.visible).map((legend) => legend.name);
        return Object.fromEntries(
            Object.entries(transformedData).filter(([key]) => visibleKeys.includes(key)),
        ) as IDomainsData;
    }, [transformedData, legends]);

    const series = useMemo(
        () => processDataDomainToShow(visibleData, data, isPercentageDataMode, trafficSourcesMap),
        [visibleData, data, isPercentageDataMode, trafficSourcesMap],
    );

    const config = useMemo(() => getChartConfig(series, isPercentageDataMode), [series, isPercentageDataMode]);

    const onLegendClick = useCallback(
        (clicked: ILegendItem) => {
            const updatedLegends = legends.map((legend) =>
                legend.name === clicked.name ? { ...legend, visible: !legend.visible } : legend,
            );
            setLegends(updatedLegends);
        },
        [legends],
    );

    return (
        <ViewByDomainChartWrapper data-automation="ViewByDomainChart">
            <ChartWrapperWithKey dependency={series}>
                <ChannelsOverviewBarChart
                    data={series}
                    config={config}
                    dataAutomation="ChannelsOverviewBarChart"
                />
            </ChartWrapperWithKey>
            <LegendContainer>
                {childrenAboveLegend}
                <FlexRow>{getLegends(legends, onLegendClick)}</FlexRow>
            </LegendContainer>
        </ViewByDomainChartWrapper>
    );
};

export default ViewByDomainChart;
